import React from 'react';

const codeClass = 'cy:px-0.5 cy:py-0 cy:bg-gray-100 cy:rounded cy:text-xs cy:font-mono';

/**
 * Renders inline formatting only (**bold**, `code`). Returns array of React nodes.
 */
function formatInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const regex = /(\*\*.+?\*\*|`[^`]*`)/g;
  let lastIndex = 0;
  let keyIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(<React.Fragment key={`${keyPrefix}-t-${keyIndex++}`}>{text.slice(lastIndex, match.index)}</React.Fragment>);
    }
    if (match[0].startsWith('**')) {
      out.push(<strong key={`${keyPrefix}-s-${keyIndex++}`}>{match[0].slice(2, -2)}</strong>);
    } else if (match[0].startsWith('`')) {
      out.push(<code key={`${keyPrefix}-c-${keyIndex++}`} className={codeClass}>{match[0].slice(1, -1)}</code>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    out.push(<React.Fragment key={`${keyPrefix}-t-${keyIndex++}`}>{text.slice(lastIndex)}</React.Fragment>);
  }
  return out;
}

/**
 * Renders a paragraph segment: newlines → <br />, plus **bold** and `code`.
 */
function formatParagraph(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*.+?\*\*|`[^`]*`|\n)/g;
  let lastIndex = 0;
  let keyIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<React.Fragment key={`${keyPrefix}-t-${keyIndex++}`}>{text.slice(lastIndex, match.index)}</React.Fragment>);
    }
    if (match[0] === '\n') {
      parts.push(<br key={`${keyPrefix}-b-${keyIndex++}`} />);
    } else if (match[0].startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-s-${keyIndex++}`}>{match[0].slice(2, -2)}</strong>);
    } else if (match[0].startsWith('`')) {
      parts.push(<code key={`${keyPrefix}-c-${keyIndex++}`} className={codeClass}>{match[0].slice(1, -1)}</code>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(<React.Fragment key={`${keyPrefix}-t-${keyIndex++}`}>{text.slice(lastIndex)}</React.Fragment>);
  }
  return parts;
}

/**
 * Renders issue text (description, dev_help, layman_help, etc.) supporting both
 * plain text and simple markdown so WCAG (plain) and best-practice (markdown) data display correctly.
 * - **bold** → <strong>
 * - `code` → <code>
 * - Newlines → <br />
 * - Lines starting with "- " (after a newline) → <ul><li>...</li></ul>
 * Plain text with no markdown is rendered unchanged.
 */
export interface FormattedIssueTextProps {
  text: string | null | undefined;
  className?: string;
  style?: React.CSSProperties;
  as?: 'p' | 'div' | 'span';
}

export const FormattedIssueText: React.FC<FormattedIssueTextProps> = ({
  text,
  className = '',
  style,
  as: Tag = 'p',
}) => {
  if (text == null || text === '') return null;

  const blocks = text.split(/\n\n+/);
  const keyPrefix = 'fmt';
  const nodes: React.ReactNode[] = [];

  blocks.forEach((block, blockIdx) => {
    const lines = block.split('\n');
    const bulletLines = lines.filter((line) => /^\s*-\s+/.test(line));
    const isListBlock = lines.length > 0 && bulletLines.length === lines.length;

    if (isListBlock && lines.length > 0) {
      nodes.push(
        <ul key={`${keyPrefix}-ul-${blockIdx}`} className="cy:list-disc cy:pl-5 cy:my-2 cy:space-y-1">
          {lines.map((line, i) => {
            const content = line.replace(/^\s*-\s+/, '');
            return (
              <li key={`${keyPrefix}-li-${blockIdx}-${i}`}>
                {formatInline(content, `${keyPrefix}-${blockIdx}-${i}`)}
              </li>
            );
          })}
        </ul>
      );
    } else {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-p-${blockIdx}`}>
          {formatParagraph(block, `${keyPrefix}-${blockIdx}`)}
        </React.Fragment>
      );
    }
  });

  return (
    <Tag className={className} style={style}>
      {nodes}
    </Tag>
  );
};
